import {
    faCircle,
    faPaperclip,
    faPaperPlaneTop,
    faPlus,
    faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { faBellSlash, faDownload, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { SideBarStates, useSidebarDispatch } from '../../../context/SidebarContext';
import { BasePanelComponent } from './CommonComponents';
import { CreateChatModal } from '@components/modals/chatModal/CreateChatModal';
import { EditMembersModal } from '@components/modals/chatModal/EditMembersModal';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { LottieLoader } from '@components/loader/LottieLoader';
import {
    Conversation,
    CourseMemberGroupEnum,
    FileEntry,
    useConversationMessagesQuery,
    useCreateConversationMessageMutation,
    useDeleteConversationMessageMutation,
    useHideConversationMutation,
    useLeaveConversationMutation,
    useReopenConversationMutation,
    useToggleMuteConversationMutation,
    useToggleViewConversationMutation,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { SkipperAvatar } from '@skipper/ui';
import dayjs from 'dayjs';
import { Button, Dropdown } from '@components/base';
import { useQueryClient } from 'react-query';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { ImageSelectModal } from '@components/modals/imageSelectModal/ImageSelectModal';
import { SimpleModal } from '@components/base/overlays/Modal/SimpleModal';
import { EditChatNameModal } from '@components/modals/chatModal/EditChatNameModal';
import Linkify from 'react-linkify';
import TextareaAutosize from 'react-textarea-autosize';
import { useRouter } from 'next/router';
import { useConversationsData } from '@hooks/useConversationsData';
import { FileListAttachment } from '@components/activities/AssignmentSubmissionsView';
import { prettyDateOrTime } from '@utils/dateFormatter';
import { AnimatedSearchField } from '@components/form/animated/AnimatedSearchField';
import { useDebounce } from '@hooks/useDebounce';
import { transformString } from '@utils/transformString';
import {WithTooltip} from "@components/common/tooltip/SkipperTooltip";

export function ChatPanel() {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [conversationsList, setConversationsList] = useState<any>([]);
    const { toggle } = useUniversalModalDispatch();
    const { conversationsData, conversationsLoading, conversationsRefetching } =
        useConversationsData();

    const queryClient = useQueryClient();
    const [currentUser] = useUserSession();
    const router = useRouter();
    const { toggle: sidebarToggle } = useSidebarDispatch();

    const [currentChatId, setCurrentChatId] = React.useState<string | null>(null);
    const currentChat = conversationsData?.conversations.find(
        (conversation) => conversation.id === currentChatId,
    ) as Conversation;

    useEffect(() => {
        queryClient.resetQueries('conversationsList');
        if (currentUser && router.query.conversation) {
            sidebarToggle(SideBarStates.Chat);
            setCurrentChatId(router.query.conversation as string);
            const newQueryParams = new URLSearchParams(router.query as Record<string, string>);
            newQueryParams.delete('conversation');
            router.replace({
                    pathname: router.pathname,
                    query: {
                        ...Object.fromEntries(newQueryParams.entries()),
                    }
                }, undefined, { shallow: true });
        }
    }, [currentUser?.id, router.query.conversation]);

    const debounceSearchKeyword = useDebounce(
        searchTerm.replace(/\s+/g, '').trim().toLowerCase(),
        500,
    );

    useEffect(() => {
        if (debounceSearchKeyword) {
            const filteredTitles = conversationsData?.conversations?.filter(
                (conversation) =>
                    transformString(conversation.title).includes(debounceSearchKeyword) ||
                    conversation.participants?.find((participant) =>
                        transformString(participant.fullName).includes(debounceSearchKeyword),
                    ) ||
                    conversation.participatingCourseMemberGroups?.find(
                        (group) =>
                            transformString(group.course.title).includes(debounceSearchKeyword) ||
                            group.memberGroupTypes.find((gt) =>
                                transformString(gt).includes(debounceSearchKeyword),
                            ),
                    ),
            );

            setConversationsList(filteredTitles);
        } else {
            setConversationsList(conversationsData?.conversations || []);
        }
    }, [debounceSearchKeyword, conversationsData?.conversations]);

    return (
        <BasePanelComponent openState={SideBarStates.Chat}>
            {currentChat ? (
                <>
                    <BasePanelComponent.Header
                        title={currentChat.title}
                        content={
                            <div className={'flex items-center gap-4'}>
                                <ConversationActions conversation={currentChat} insideChat />
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    size="xl"
                                    className="text-gray-500 cursor-pointer hover:text-teal-600"
                                    onClick={() => setCurrentChatId(null)}
                                />
                            </div>
                        }></BasePanelComponent.Header>
                    <BasePanelComponent.Body className="">
                        <ChatView currentChat={currentChat} />
                    </BasePanelComponent.Body>
                </>
            ) : (
                <>
                    <BasePanelComponent.Header
                        title="Chat inbox"
                        content={
                            <div className="flex items-center gap-x-2">
                                <AnimatedSearchField
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                    }}
                                    onClose={() => {
                                        setSearchTerm('');
                                    }}
                                    size="small"
                                    placeholder="Search"
                                />
                                <div
                                    className="cursor-pointer px-2 py-1 rounded-md hover:bg-teal-100 text-gray-500 hover:text-teal-700"
                                    onClick={() => toggle(true, <CreateChatModal />)}>
                                    <FontAwesomeIcon icon={faPlus} size="lg" />
                                </div>
                            </div>
                        }></BasePanelComponent.Header>
                    <BasePanelComponent.Body className="">
                        {conversationsLoading || !conversationsData ? (
                            <div className="flex flex-col px-6 pt-20 items-center text-center">
                                <LottieLoader />
                            </div>
                        ) : conversationsList.length > 0 ? (
                            conversationsList.map((conversation) => (
                                <ConversationRow
                                    conversation={conversation as Conversation}
                                    key={conversation.id}
                                    onSelect={() => setCurrentChatId(conversation.id)}
                                />
                            ))
                        ) : (
                            <div className="flex px-4 py-8 text-gray-400 items-center justify-center text-lg">
                                No conversations.
                                <span
                                    className="text-teal-600 ml-1 cursor-pointer"
                                    onClick={() => toggle(true, <CreateChatModal />)}>
                                    Start New
                                </span>
                            </div>
                        )}
                    </BasePanelComponent.Body>
                </>
            )}
        </BasePanelComponent>
    );
}

function ConversationRow({
    conversation,
    onSelect,
}: {
    conversation: Conversation;
    onSelect: () => void;
}) {
    const lastActiveDate = conversation.lastMessage?.createdAt || conversation.createdAt;

    return (
        <div
            onClick={onSelect}
            className={`${
                conversation.isClosed ? 'bg-gray-100' : ''
            } py-4 px-4 border-b cursor-pointer group hover:bg-teal-50 relative`}>
            <div className="flex justify-between gap-3 items-center">
                <div className={'w-8'}>
                    {!conversation.isViewed && (
                        <FontAwesomeIcon
                            icon={faCircle}
                            size={'2xs'}
                            className={'absolute top-1 left-1 text-lime-500'}
                        />
                    )}
                    {conversation.isViewed && conversation.isMuted && (
                        <FontAwesomeIcon
                            icon={faBellSlash}
                            size={'xs'}
                            className={'absolute top-1 left-1 text-gray-400'}
                            title="Chat is muted"
                        />
                    )}
                    <ConversationIcon conversation={conversation} />
                </div>
                <div className={'flex-grow font-medium truncate'}>{conversation.title}</div>
                <div className={'text-xs flex items-center gap-3'}>
                    <WithTooltip content={dayjs(lastActiveDate).format('MMM D, YYYY h:mm A')}>
                        <div className={'text-gray-400 w-16 text-right'}>
                            {prettyDateOrTime(lastActiveDate)}
                        </div>
                    </WithTooltip>
                    <ConversationActions conversation={conversation} />
                </div>
            </div>
            <div className="flex gap-3">
                <div className={'w-8 flex-shrink-0'}></div>
                <div className={'font-normal text-gray-400 text-sm truncate'}>
                    {conversation.lastMessage?.content || (
                        <div className={'text-zinc-400 italic text-xs'}>
                            {conversation.lastMessage?.fileEntry ? 'Attachment' : 'No messages'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ConversationIcon({ conversation }: { conversation: Conversation }) {
    const otherParticipants = conversation.participants.filter(
        (p) => p.id !== conversation.owner.id,
    );
    const primaryParticipant = otherParticipants.length == 1 ? otherParticipants[0] : undefined;
    const primaryCourse =
        conversation.courseParticipantEnrollments.length == 1
            ? conversation.courseParticipantEnrollments[0].course
            : undefined;

    if (conversation.iconFileEntry)
        return (
            <SkipperAvatar
                size="medium"
                imageUrl={conversation.iconFileEntry.croppedUrl}
                calendarColor={(primaryParticipant || conversation.owner).calendarColor}
                initials={(primaryParticipant || conversation.owner).initials}
            />
        );
    else if (primaryCourse)
        return (
            <SkipperAvatar
                size="medium"
                imageUrl={undefined}
                calendarColor={primaryCourse.iconColor}
                initials={primaryCourse.title
                    .toUpperCase()
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)}
            />
        );
    else if (primaryParticipant)
        return (
            <SkipperAvatar
                size="medium"
                imageUrl={primaryParticipant.imageFileEntry?.croppedUrl}
                calendarColor={primaryParticipant.calendarColor}
                initials={primaryParticipant.initials}
            />
        );
    else
        return (
            <SkipperAvatar
                size="medium"
                imageUrl={conversation.iconFileEntry?.croppedUrl}
                calendarColor={conversation.owner.calendarColor}
                initials={conversation.owner.initials}
            />
        );
}

function ConversationActions({
    conversation,
    insideChat = false,
}: {
    conversation: Conversation;
    insideChat?: boolean;
}) {
    const chatDropdownItemClasses = 'flex w-full px-3 py-1.5 hover:text-teal-600 cursor-pointer';
    const queryClient = useQueryClient();
    const { toggle } = useUniversalModalDispatch();
    const [currentUser] = useUserSession();

    const defaultMutationOptions = {
        onSettled: (data) => {
            queryClient.invalidateQueries('conversationsList');
            queryClient.invalidateQueries(['conversationMessages', { id: conversation.id }]);
        },
    };
    const toggleViewMutation = useToggleViewConversationMutation(
        getClient('communication'),
        {
            ...defaultMutationOptions,
            onMutate: (vars) => {
                queryClient.setQueryData(['conversationsList'], (oldData: any) => {
                    const conversation = oldData.conversations.find((c) => c.id == vars.id);
                    conversation.isViewed = !conversation.isViewed;
                    return oldData;
                })
            }
        }
    );
    const toggleMuteMutation = useToggleMuteConversationMutation(
        getClient('communication'),
        {
            ...defaultMutationOptions,
            onMutate: (vars) => {
                queryClient.setQueryData(['conversationsList'], (oldData: any) => {
                    const conversation = oldData.conversations.find((c) => c.id == vars.id);
                    conversation.isMuted = !conversation.isMuted;
                    return oldData;
                })
            }
        }
    );
    const leaveConversationMutation = useLeaveConversationMutation(
        getClient('communication'),
        {
            ...defaultMutationOptions,
            onMutate: (vars) => {
                queryClient.setQueryData(['conversationsList'], (oldData: any) => {
                    const conversation = oldData.conversations.find((c) => c.id == vars.id);
                    conversation.isClosed = true;
                    return oldData;
                })
            }
        }
    );
    const hideConversationMutation = useHideConversationMutation(
        getClient('communication'),
        defaultMutationOptions,
    );
    const reopenConversationMutation = useReopenConversationMutation(
        getClient('communication'),
        {
            ...defaultMutationOptions,
            onMutate: (vars) => {
                queryClient.setQueryData(['conversationsList'], (oldData: any) => {
                    const conversation = oldData.conversations.find((c) => c.id == vars.id);
                    conversation.isClosed = false;
                    return oldData;
                })
            }
        }
    );

    return (
        <Dropdown
            options={[
                !conversation.isClosed && (
                    <button
                        className={chatDropdownItemClasses}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleViewMutation.mutate({ id: conversation.id });
                        }}>
                        {conversation.isViewed ? 'Mark as Unread' : 'Mark as Read'}
                    </button>
                ),
                !conversation.isClosed &&
                    conversation.permissions.includes('manage') &&
                    insideChat && (
                        <button
                            className={chatDropdownItemClasses}
                            onClick={() =>
                                toggle(true, <EditChatNameModal conversation={conversation} />)
                            }>
                            Chat Name
                        </button>
                    ),
                !conversation.isClosed &&
                    conversation.permissions.includes('manage') &&
                    insideChat && (
                        <button
                            className={chatDropdownItemClasses}
                            onClick={() =>
                                toggle(true, <EditMembersModal conversation={conversation} />)
                            }>
                            Edit Members
                        </button>
                    ),
                !conversation.isClosed && (
                    <button
                        className={chatDropdownItemClasses}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggle(
                                true,
                                <SimpleModal
                                    type="warning"
                                    title={conversation.isMuted ? 'Unmute Chat' : 'Mute Chat'}
                                    body={
                                        conversation.isMuted
                                            ? 'Do you want to unmute notifications for this chat? You will start receiving new message notifications for this chat.'
                                            : 'Do you want to mute notifications for this chat? You will not receive new message notifications for this chat.'
                                    }
                                    confirmLabel={
                                        conversation.isMuted ? 'Unmute Chat' : 'Mute Chat'
                                    }
                                    onConfirm={() => {
                                        toggleMuteMutation.mutate({ id: conversation.id });
                                    }}
                                />,
                            );
                        }}>
                        {conversation.isMuted ? 'Unmute Chat' : 'Mute Chat'}
                    </button>
                ),
                conversation.permissions.includes('manage') && (
                    <button
                        className={chatDropdownItemClasses}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (conversation.isClosed)
                                reopenConversationMutation.mutate({ id: conversation.id });
                            else leaveConversationMutation.mutate({ id: conversation.id });
                        }}>
                        {conversation.isClosed ? 'Reopen Chat' : 'Close Chat'}
                    </button>
                ),
                !conversation.permissions.includes('manage') &&
                    conversation.participatingUsers.find((u) => u.id == currentUser.id) && (
                        <button
                            className={chatDropdownItemClasses}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggle(
                                    true,
                                    <SimpleModal
                                        type="warning"
                                        title="Leave Chat"
                                        body="Once you leave this chat you will not be able to post messages or interact with chat members."
                                        confirmLabel="Leave Chat"
                                        onConfirm={() => {
                                            leaveConversationMutation.mutate({
                                                id: conversation.id,
                                            });
                                        }}
                                    />,
                                );
                            }}>
                            Leave Chat
                        </button>
                    ),
                (conversation.permissions.includes('manage') ||
                    !conversation.participants.find((u) => u.id == currentUser.id) ||
                    conversation.isClosed) && (
                    <button
                        className={`${chatDropdownItemClasses} border-t border-gray-200 flex py-2 hover:text-red-600 hover:bg-transparent focus:text-red-600`}
                        key={''}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggle(
                                true,
                                <SimpleModal
                                    type="warning"
                                    title="Delete Chat"
                                    body={conversation.permissions.includes('manage') ?
                                        "Deleting this chat will close the chat and remove it from your Chats. Members will have still access to this chat but will not be able to post new messages." :
                                        "Deleting this chat will remove it from your Chats"}
                                    confirmLabel="Delete Chat"
                                    onConfirm={() => {
                                        hideConversationMutation.mutate({ id: conversation.id });
                                    }}
                                />,
                            );
                        }}>
                        Delete Chat
                    </button>
                ),
            ].filter((c) => c)}
            ellipse={true}
            label={undefined}
            right={true}
            classContainer={
                'px-0 py-0 ring-0 outline-none focus:ring-0 focus:outline-none focus:ring-offset-0 focus:ring-offset-transparent'
            }
            classDropdown={'w-36 text-sm'}
        />
    );
}

function ChatView({ currentChat }: { currentChat: Conversation }) {
    const currentChatId = currentChat.id;
    const [currentUser] = useUserSession();
    const queryClient = useQueryClient();
    const { toggle } = useUniversalModalDispatch();
    const [subjectLineOpen, setSubjectLineOpen] = useState(false);
    const subjectRef = useRef<HTMLInputElement>(null);

    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionText, setMentionText] = useState('');
    const [selectedMentionIdx, setSelectedMentionIdx] = useState(-1);
    const courseMemberGroupTypes = currentChat.participatingCourseMemberGroups
        .map((c) => c.memberGroupTypes)
        .flat();
    const mentionList = (
        currentChat.courseParticipantEnrollments.length > 0
            ? [
                  courseMemberGroupTypes.includes(CourseMemberGroupEnum.Student) ? 'students' : '',
                  courseMemberGroupTypes.includes(CourseMemberGroupEnum.Parent) ? 'parents' : '',
                  courseMemberGroupTypes.includes(CourseMemberGroupEnum.Leader)
                      ? 'courseleaders'
                      : '',
                  'moderators',
              ].filter((m) => m.length > 0)
            : []
    )
        .concat(['everyone'])
        .concat(mentionText.length > 0 ? currentChat.participants.map((p) => p.fullName) : []);
    const filteredMentionList = mentionList.filter(
        (m) => !mentionText || m.toLowerCase().startsWith(mentionText.toLowerCase()),
    );
    const [prevFilteredMentionList, setPrevFilteredMentionList] = useState<string[]>([]);
    if (filteredMentionList.join() != prevFilteredMentionList.join()) {
        setSelectedMentionIdx(-1);
        setPrevFilteredMentionList(filteredMentionList);
    }

    const chatMessages = useConversationMessagesQuery(
        getClient('communication'),
        { id: currentChatId },
        {
            onSettled: () => queryClient.invalidateQueries('conversationsList'),
        },
    );
    const contentRef = useRef(null);
    useEffect(() => {
        if (contentRef.current) {
            (Array.from(contentRef.current.children).slice(-1)[0] as HTMLElement).scrollIntoView();
        }
    }, [chatMessages.data?.conversationMessages?.map((m) => m.id)?.join(',')]);

    const deleteMessageMutation = useDeleteConversationMessageMutation(getClient('communication'), {
        onSettled: () => {
            queryClient.invalidateQueries('conversationsList');
            queryClient.invalidateQueries(['conversationMessages', { id: currentChatId }]);
        },
    });

    const sendMessageMutation = useCreateConversationMessageMutation(getClient('communication'), {
        onSettled: () => {
            queryClient.invalidateQueries('conversationsList');
            queryClient.invalidateQueries(['conversationMessages', { id: currentChatId }]);
            messageBoxRef.current.value = '';
            setAttachmentEntry(null);
            setSubjectLineOpen(false);
            if (subjectRef.current) subjectRef.current.value = '';
        },
    });
    const messageBoxRef = useRef(null);
    const sendMessageHandler = () => {
        const message = messageBoxRef.current.value;
        if (message || attachmentEntry) {
            sendMessageMutation.mutate({
                conversationId: currentChatId,
                content: message,
                subject: subjectLineOpen ? subjectRef.current?.value : undefined,
                fileEntryId: attachmentEntry?.id,
            });
        }
    };
    const [attachmentEntry, setAttachmentEntry] = React.useState<FileEntry>(null);

    return chatMessages.isLoading || !chatMessages.data?.conversationMessages ? (
        <div className="flex flex-col px-6 pt-20 items-center text-center">
            <LottieLoader />
        </div>
    ) : (
        <>
            <div className={'flex flex-col h-full'}>
                <div className="flex flex-col gap-1 flex-grow overflow-auto" ref={contentRef}>
                    {chatMessages.data.conversationMessages.map((message, i) => {
                        const prevMessage =
                            i > 0 ? chatMessages.data?.conversationMessages[i - 1] : null;
                        return (
                            <>
                                {(!prevMessage ||
                                    dayjs(prevMessage.createdAt).date() !==
                                        dayjs(message.createdAt).date()) && (
                                    <div key={message.id}>
                                        <div className="text-center text-gray-400 text-xs mt-4">
                                            {dayjs(message.createdAt).format('MMMM D, YYYY')}
                                        </div>
                                    </div>
                                )}
                                {message.author ? (
                                    <div
                                        className={`flex gap-2 px-4 py-2 items-start text-sm justify-start group`}>
                                        <div className={'w-9 flex-grow-0'}>
                                            <SkipperAvatar
                                                size="medium"
                                                imageUrl={message.author.imageFileEntry?.url}
                                                calendarColor={message.author.calendarColor}
                                                initials={message.author.initials}
                                            />
                                        </div>
                                        <div className="max-w-sm">
                                            {message.author.fullName}
                                            <div
                                                className={`flex flex-col gap-2 text-sm break-words ${
                                                    message.author.id == currentUser.id
                                                        ? 'bg-teal-100'
                                                        : 'bg-gray-100'
                                                } py-2 px-3 rounded-xl mt-1 relative`}>
                                                {message.content && (
                                                    <div>
                                                        {message.content
                                                            .split('\n')
                                                            .map((line, i) => (
                                                                <Linkify
                                                                    key={i}
                                                                    componentDecorator={(
                                                                        decoratedHref,
                                                                        decoratedText,
                                                                        key,
                                                                    ) => (
                                                                        <a
                                                                            target="_blank"
                                                                            rel={'noreferrer'}
                                                                            href={decoratedHref}
                                                                            key={key}
                                                                            className="underline">
                                                                            {decoratedText}
                                                                        </a>
                                                                    )}>
                                                                    <p
                                                                        style={{ minHeight: '1em' }}
                                                                        key={i}>
                                                                        {line}
                                                                    </p>
                                                                </Linkify>
                                                            ))}
                                                    </div>
                                                )}
                                                <div
                                                    className={`text-xs text-gray-400 text-right flex justify-between gap-4`}>
                                                    {message.fileEntry && (
                                                        <div className="text-xs text-gray-400">
                                                            1 attachment
                                                        </div>
                                                    )}
                                                    <div className="text-right flex-1">
                                                        {dayjs(message.createdAt).format('h:mm A')}
                                                    </div>
                                                </div>
                                                {(message.author.id == currentUser.id ||
                                                    currentChat.moderators.find(
                                                        (m) => m.id == currentUser.id,
                                                    )) && (
                                                    <FontAwesomeIcon
                                                        icon={faTimes}
                                                        title={'Delete message'}
                                                        onClick={() =>
                                                            deleteMessageMutation.mutate({
                                                                id: message.id,
                                                            })
                                                        }
                                                        className="absolute invisible group-hover:visible top-2 -right-5 text-gray-300 hover:text-red-600 cursor-pointer"
                                                    />
                                                )}
                                            </div>
                                            {message.fileEntry && (
                                                <div className={`mt-2`}>
                                                    <FileListAttachment
                                                        item={
                                                            {
                                                                file: message.fileEntry as any,
                                                            } as any
                                                        }
                                                        showDelete={false}
                                                        showPreview={true}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="py-2 text-center text-xs text-gray-400">
                                        {dayjs(message.createdAt).format('D MMM YYYY h:mm A')}
                                        <div>{message.content}</div>
                                    </div>
                                )}
                            </>
                        );
                    })}
                </div>
                {!currentChat.isClosed && (
                    <div className={`px-3 py-3 flex-grow-0 h-42 bg-white border-t`}>
                        <div>
                            {mentionOpen && (
                                <div className="relative">
                                    <div
                                        className={
                                            'absolute bottom-0 bg-white shadow-xl rounded-lg text-sm flex flex-col justify-between border overflow-hidden'
                                        }>
                                        {filteredMentionList
                                            .map((p, i) => (
                                                <button
                                                    key={i}
                                                    className={`px-3 py-1 text-left hover:text-teal-700 cursor-pointer hover:bg-teal-50 ${
                                                        selectedMentionIdx == i ? 'bg-teal-100' : ''
                                                    }`}
                                                    onClick={() => {
                                                        setMentionOpen(false);
                                                        setSelectedMentionIdx(-1);
                                                        messageBoxRef.current.value =
                                                            messageBoxRef.current.value.replace(
                                                                new RegExp(`@${mentionText}$`),
                                                                `@${p} `,
                                                            );
                                                        messageBoxRef.current.focus();
                                                    }}>
                                                    {p}
                                                </button>
                                            ))
                                            .filter((a) => a)}
                                    </div>
                                </div>
                            )}
                            <TextareaAutosize
                                placeholder={'Enter your message'}
                                ref={messageBoxRef}
                                disabled={sendMessageMutation.isLoading}
                                minRows={2}
                                maxRows={10}
                                onKeyDown={(e) => {
                                    if (
                                        (e.key == 'ArrowUp' || e.key == 'ArrowDown') &&
                                        mentionOpen
                                    ) {
                                        e.preventDefault();
                                        if (e.key == 'ArrowUp') {
                                            if (selectedMentionIdx == -1)
                                                setSelectedMentionIdx(
                                                    filteredMentionList.length - 1,
                                                );
                                            else
                                                setSelectedMentionIdx(
                                                    Math.max(0, selectedMentionIdx - 1),
                                                );
                                        } else
                                            setSelectedMentionIdx(
                                                Math.min(
                                                    filteredMentionList.length - 1,
                                                    selectedMentionIdx + 1,
                                                ),
                                            );
                                    } else if (
                                        (e.key == 'Enter' || e.key == 'Tab') &&
                                        mentionOpen &&
                                        selectedMentionIdx >= 0
                                    ) {
                                        e.preventDefault();
                                        setMentionOpen(false);
                                        setSelectedMentionIdx(-1);
                                        messageBoxRef.current.value =
                                            messageBoxRef.current.value.replace(
                                                new RegExp(`@${mentionText}$`),
                                                `@${filteredMentionList[selectedMentionIdx]} `,
                                            );
                                    } else if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        sendMessageHandler();
                                    }
                                }}
                                onKeyUp={(e) => {
                                    const lastMention = messageBoxRef.current.value
                                        .split('@')
                                        .slice(-1)[0];
                                    setMentionText(lastMention?.split(' ')?.[0]);
                                    const newMentionOpen =
                                        messageBoxRef.current.value.length > 0
                                            ? lastMention?.split(' ')?.length == 1
                                            : false;
                                    setMentionOpen(newMentionOpen);
                                    if (!newMentionOpen) setSelectedMentionIdx(-1);
                                }}
                                className={
                                    'px-2 py-1 w-full h-16 focus:outline-teal-600'
                                }></TextareaAutosize>
                            {subjectLineOpen && (
                                <input
                                    type="text"
                                    ref={subjectRef}
                                    className={'px-2 py-2 mb-2 w-full focus:outline-teal-600'}
                                    placeholder={'Enter email notification subject'}
                                />
                            )}
                        </div>
                        <div className={'flex justify-between items-center'}>
                            <div className={'flex gap-3'}>
                                {attachmentEntry ? (
                                    <div className={'text-sm'}>
                                        {attachmentEntry.title}
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            className="ml-1 text-red-600 cursor-pointer"
                                            onClick={() => setAttachmentEntry(null)}
                                        />
                                    </div>
                                ) : (
                                    <button
                                        className={
                                            'text-sm cursor-pointer hover:text-teal-600 focus:outline-0'
                                        }
                                        onClick={() => {
                                            toggle(
                                                true,
                                                <ImageSelectModal
                                                    updater={(fileId, fileEntry) => {
                                                        setAttachmentEntry(fileEntry);
                                                        toggle(false);
                                                    }}
                                                    allowCropping={false}
                                                    allowedFileTypes="any"
                                                    isImmediateUpdate={true}
                                                />,
                                            );
                                        }}>
                                        <FontAwesomeIcon icon={faPaperclip} className="mr-2" />
                                        Attach File
                                    </button>
                                )}
                                <div className={'text-sm ml-2 flex-grow'}>
                                    <button
                                        className={
                                            'text-sm cursor-pointer hover:text-teal-600 focus:outline-0'
                                        }
                                        onClick={() => setSubjectLineOpen(!subjectLineOpen)}>
                                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                        {subjectLineOpen
                                            ? 'Remove Email Subject'
                                            : 'Add Email Subject'}
                                    </button>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="primary"
                                size="small"
                                disabled={sendMessageMutation.isLoading}
                                className="flex gap-x-4"
                                onClick={sendMessageHandler}>
                                Send
                                <FontAwesomeIcon icon={faPaperPlaneTop} className="" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
